/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';

import { getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';
import { ProductsDataActions, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ProductSearchResultExt } from '../../../shared-utils/types';
import { applyAttributesToProduct } from '../../../shared-utils/attributes';

export class GetAlternativeProductsExperimentalInput implements Msdyn365.IActionInput {

    public productId: number;

    constructor(productId: number) {
        this.productId = productId;
    }

    public getCacheKey = () => `AlternativeProductsExperimental-${this.productId}`;
    public getCacheObjectType = () => 'ProductSearchResult';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const productId = getSelectedProductIdFromActionInput(args);

    if (productId) {
        return new GetAlternativeProductsExperimentalInput(+productId);
    }

    throw new Error('Unable to create GetAlternativeProductsExperimentalInput: productId is not defined.');
};

async function action(input: GetAlternativeProductsExperimentalInput, ctx: Msdyn365.IActionContext): Promise<ProductSearchResult[]> {
    if (ctx.requestContext.app.config?.enableAlternativeProductsFeature !== true) {
        return [];
    }

    const { channelId, catalogId } = ctx.requestContext.apiSettings;
    const relationTypes = await ProductsDataActions.getRelationTypesAsync(
        { callerContext: ctx },
        input.productId,
        channelId,
        catalogId
    );

    if (relationTypes.length === 0) {
        return [];
    }

    const promises = relationTypes.map(relationType => ProductsDataActions.getRelatedProductsAsync(
        { callerContext: ctx },
        input.productId,
        channelId,
        catalogId,
        relationType.RecordId
    ));

    const results = await Promise.all(promises);
    const productResults: ProductSearchResultExt[] = [];

    results.forEach((p) => {
        productResults.push(...p);
    });

    // Sort by DisplayOrder ascending
    productResults.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

    if (results.length === 0) {
        return [];
    }

    productResults.forEach((p) => {
        applyAttributesToProduct(ctx.requestContext, p, p.AttributeValues, undefined);
    });

    return productResults;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ProductSearchResult[]>>action,
    id: 'GetAlternativeProductsExperimental',
    input: createInput
});
